import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Banknote, Package, Users, FolderOpen, Settings, UserCheck, Headphones, User, Globe, Puzzle, BarChart3, Brain } from 'lucide-react';

const FeaturesSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const features = [
    {
      icon: <Banknote className="h-12 w-12 text-blue-600" />,
      title: "Finanzmanagement",
      subtitle: "Komplette Buchhaltung und Finanzsteuerung",
      description: "Alle wichtigen Finanzprozesse in einem System - von der Buchhaltung bis zur Finanzanalyse.",
      details: [
        "Buchhaltung (Debitoren, Kreditoren, Hauptbuch)",
        "Finanzberichte und -analysen",
        "Kostenrechnung und Controlling",
        "Zahlungsabwicklung (Bankkonten, SEPA, etc.)",
        "Anlagenbuchhaltung",
        "Währungsmanagement für internationale Geschäfte"
      ],
      highlight: "Vollständige Finanzübersicht in Echtzeit"
    },
    {
      icon: <Package className="h-12 w-12 text-green-600" />,
      title: "Lager & Logistik",
      subtitle: "Intelligente Lagerverwaltung",
      description: "Behalte deine Bestände im Blick und optimiere deine Lagerlogistik automatisch.",
      details: [
        "Artikelverwaltung mit Varianten",
        "Lagerplätze, Lagerorte, Umlagerung",
        "Inventur und Lagerbewertungen",
        "Kommissionierung und Wareneingang",
        "Serien- und Chargennummernverwaltung",
        "Automatische Nachbestellvorschläge"
      ],
      highlight: "Live-Lagerbestände in Echtzeit"
    },
    {
      icon: <Users className="h-12 w-12 text-purple-600" />,
      title: "Einkauf & Verkauf",
      subtitle: "Vom Angebot bis zur Rechnung",
      description: "Verwalte deine Kunden und Lieferanten effizient - alle Verkaufs- und Einkaufsprozesse automatisiert.",
      details: [
        "Angebots- und Auftragsmanagement",
        "Lieferanten- und Kundenverwaltung",
        "Einkaufs- und Verkaufsrechnungen",
        "Rahmenaufträge und Verträge",
        "Preis- und Rabattverwaltung",
        "Rücksendungen und Gutschriften"
      ],
      highlight: "Automatische Rechnungserstellung"
    },
    {
      icon: <FolderOpen className="h-12 w-12 text-orange-600" />,
      title: "Projektmanagement",
      subtitle: "Projekte erfolgreich steuern",
      description: "Plane, verfolge und rechne deine Projekte ab - mit vollständiger Kosten- und Zeiterfassung.",
      details: [
        "Projektplanung und -verfolgung",
        "Zeit- und Kostenerfassung",
        "Projektabrechnung und Fakturierung",
        "Budgetierung und Fortschrittskontrolle",
        "Ressourcenplanung",
        "Projektberichte und Analysen"
      ],
      highlight: "Rentabilität jedes Projekts im Blick"
    },
    {
      icon: <Settings className="h-12 w-12 text-red-600" />,
      title: "Produktion & Fertigung",
      subtitle: "Effiziente Produktionssteuerung",
      description: "Plane und steuere deine Produktion optimal - von der Stückliste bis zum fertigen Produkt.",
      details: [
        "Stücklisten und Arbeitspläne",
        "Produktionsaufträge und -planung",
        "Kapazitätsplanung und -optimierung",
        "Fertigungsaufträge, Nachkalkulation",
        "Bedarfsplanung (MRP)",
        "Qualitätskontrolle"
      ],
      highlight: "Automatische Bedarfsplanung"
    },
    {
      icon: <UserCheck className="h-12 w-12 text-teal-600" />,
      title: "CRM (light Version)",
      subtitle: "Grundlegende Kundenbeziehungen",
      description: "Wichtig: Business Central ist KEIN vollwertiges CRM-System, bietet aber grundlegende CRM-Funktionen für kleinere Unternehmen.",
      details: [
        "Kontakte und Verkaufschancen verwalten",
        "Aufgabenmanagement für Vertrieb",
        "Integration in Outlook (Verkaufschancen aus E-Mails)",
        "Kundenhistorie & Aktivitätenübersicht",
        "Einfache Kampagnenverwaltung",
        "Basis-Reporting für Vertriebsaktivitäten"
      ],
      highlight: "⚠️ Für umfangreiches CRM separate Lösung nötig"
    },
    {
      icon: <Headphones className="h-12 w-12 text-indigo-600" />,
      title: "Servicemanagement",
      subtitle: "Kundenservice optimieren",
      description: "Verwalte Serviceanfragen und Wartungsverträge professionell und effizient.",
      details: [
        "Serviceaufträge und -artikel",
        "Vertragsverwaltung und Wartungspläne",
        "Einsatzplanung für Techniker",
        "Serviceberichte und Dokumentation",
        "Ersatzteilmanagement",
        "Kundenzufriedenheit messen"
      ],
      highlight: "Schnelle Reaktionszeiten garantiert"
    },
    {
      icon: <BarChart3 className="h-12 w-12 text-violet-600" />,
      title: "Power BI & Business Intelligence",
      subtitle: "Daten visualisieren und analysieren",
      description: "Verwandle deine Geschäftsdaten in aussagekräftige Berichte und Dashboards für bessere Entscheidungen.",
      details: [
        "Vorgefertigte Power BI Berichte",
        "Interaktive Dashboards und Visualisierungen",
        "Echtzeit-Datenanalyse",
        "KPI-Überwachung und Alerts",
        "Automatische Berichtserstellung",
        "Mobile BI-Apps für unterwegs"
      ],
      highlight: "Datenbasierte Entscheidungen in Echtzeit"
    },
    {
      icon: <Brain className="h-12 w-12 text-emerald-600" />,
      title: "KI - Microsoft Copilot",
      subtitle: "Künstliche Intelligenz im ERP",
      description: "Microsoft Copilot bringt KI-Power direkt in Business Central und macht deine tägliche Arbeit effizienter.",
      details: [
        "Intelligente Datenanalyse und Insights",
        "Automatische Texterstellung für Berichte",
        "KI-gestützte Prognosen und Empfehlungen",
        "Natürliche Sprachbefehle für Abfragen",
        "Automatische Kategorisierung von Daten",
        "Intelligente Workflow-Vorschläge"
      ],
      highlight: "Die Zukunft der Geschäftssoftware - heute verfügbar"
    },
    {
      icon: <User className="h-12 w-12 text-pink-600" />,
      title: "Personalwesen (rudimentär)",
      subtitle: "Basis-Personalverwaltung",
      description: "Grundlegende Personalfunktionen - für umfangreiches HR sind Zusatzlösungen erforderlich.",
      details: [
        "Personalstammdaten verwalten",
        "Abwesenheitsverwaltung",
        "Einfache Zeiterfassung",
        "Basis-Personalberichte",
        "⚠️ Keine vollwertige Lohnabrechnung",
        "⚠️ Erweiterte HR-Funktionen nur über Zusatzlösungen"
      ],
      highlight: "Für Lohnabrechnung externe Lösung nötig"
    },
    {
      icon: <Puzzle className="h-12 w-12 text-cyan-600" />,
      title: "Integration & Erweiterbarkeit",
      subtitle: "Nahtlos vernetzt",
      description: "Business Central lässt sich perfekt in deine bestehende Microsoft-Welt integrieren und erweitern.",
      details: [
        "Microsoft 365 Integration (Outlook, Excel, Teams)",
        "Power Platform Integration (Power Apps, Power Automate)",
        "APIs und Erweiterungen verfügbar",
        "AppSource: Hunderte von Zusatzlösungen",
        "Individuelle Anpassungen möglich",
        "Nahtlose Datenintegration"
      ],
      highlight: "Unendliche Erweiterungsmöglichkeiten"
    },
    {
      icon: <Globe className="h-12 w-12 text-slate-600" />,
      title: "Internationalität & Skalierung",
      subtitle: "Global einsatzbereit",
      description: "Wachse international und skaliere dein Business ohne Systemgrenzen.",
      details: [
        "Mehrwährungs- und Mehrsprachenfähigkeit",
        "Mandantenfähigkeit für Konzerne",
        "Lokalisierungen für viele Länder",
        "Internationale Steuerregeln",
        "Globale Berichterstattung",
        "Skalierung von 1 bis 1000+ Nutzer"
      ],
      highlight: "In über 130 Ländern verfügbar"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % features.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section id="erp-features" className="py-16 md:py-24 bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Was kann Business Central <span className="text-blue-600">alles</span>?
          </h2>
          <p className="text-lg text-gray-600">
            Business Central ist wie ein Schweizer Taschenmesser für dein Unternehmen: 
            Alle wichtigen Funktionen sind dabei. Klick dich durch die verschiedenen Bereiche.
          </p>
        </div>

        {/* Slider Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {features.map((feature, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                currentSlide === index
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {feature.title}
            </button>
          ))}
        </div>

        {/* Main Slider */}
        <div className="relative max-w-5xl mx-auto px-12">
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left side - Icon and Title */}
                <div className="text-center lg:text-left">
                  <div className="flex justify-center lg:justify-start mb-6">
                    {features[currentSlide].icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-3">
                    {features[currentSlide].title}
                  </h3>
                  <p className="text-lg text-blue-600 font-semibold mb-4">
                    {features[currentSlide].subtitle}
                  </p>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {features[currentSlide].description}
                  </p>
                  
                  {/* Highlight */}
                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <p className="text-blue-800 font-semibold">
                      ✨ {features[currentSlide].highlight}
                    </p>
                  </div>
                </div>

                {/* Right side - Details */}
                <div>
                  <h4 className="text-xl font-bold mb-4 text-gray-900">
                    Das ist alles dabei:
                  </h4>
                  <ul className="space-y-3">
                    {features[currentSlide].details.map((detail, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-500 mr-3 mt-1">•</span>
                        <span className={`text-gray-600 ${detail.includes('⚠️') ? 'text-orange-600 font-medium' : ''}`}>
                          {detail}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation Arrows */}
          <Button
            onClick={prevSlide}
            variant="outline"
            size="icon"
            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white shadow-lg hover:bg-gray-50"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            onClick={nextSlide}
            variant="outline"
            size="icon"
            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white shadow-lg hover:bg-gray-50"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center mt-8 space-x-2">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                currentSlide === index ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Typical Use Cases */}
        <div className="mt-16 bg-gray-50 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-center mb-8">
            Typische Einsatzgebiete für Business Central
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl mb-2">🏪</div>
              <h4 className="font-bold text-gray-900 mb-2">Handelsunternehmen</h4>
              <p className="text-sm text-gray-600">Ein- und Verkauf, Lager, Kunden</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl mb-2">🏭</div>
              <h4 className="font-bold text-gray-900 mb-2">Produktionsbetriebe</h4>
              <p className="text-sm text-gray-600">Fertigung, Stücklisten, Planung</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl mb-2">🛠️</div>
              <h4 className="font-bold text-gray-900 mb-2">Dienstleister</h4>
              <p className="text-sm text-gray-600">Projekte, Zeit, Abrechnung</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl mb-2">🌍</div>
              <h4 className="font-bold text-gray-900 mb-2">International</h4>
              <p className="text-sm text-gray-600">Mehrere Länder, Währungen</p>
            </div>
          </div>
        </div>

        {/* Call-to-Action */}
        <div className="text-center mt-16">
          <div className="bg-blue-50 rounded-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 text-blue-900">
              Welche Funktionen brauchst du?
            </h3>
            <p className="text-blue-800 mb-6">
              Lass uns gemeinsam schauen, welche Business Central Module 
              für dein Unternehmen am wichtigsten sind.
            </p>
            <a 
              href="#contact" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Bedarfsanalyse vereinbaren
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection; 